import React, { useEffect, useRef, useImperativeHandle, forwardRef } from "react"
import { Stage, Layer } from "react-konva"
import Konva from "konva"

// Ramer-Douglas-Peucker algorithm for point simplification
const simplifyRDP = (points, epsilon) => {
  if (points.length < 3) return points

  const sqDist = (p1, p2) =>
    (p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2

  const perpendicularDistance = (pt, lineStart, lineEnd) => {
    const dx = lineEnd.x - lineStart.x
    const dy = lineEnd.y - lineStart.y
    if (dx === 0 && dy === 0) return Math.sqrt(sqDist(pt, lineStart))
    const t = ((pt.x - lineStart.x) * dx + (pt.y - lineStart.y) * dy) / (dx * dx + dy * dy)
    const nearest = {
      x: lineStart.x + t * dx,
      y: lineStart.y + t * dy,
    }
    return Math.sqrt(sqDist(pt, nearest))
  }

  const rdp = (pts, start, end, eps, keep) => {
    let maxDist = 0
    let index = start
    for (let i = start + 1; i < end; i++) {
      const dist = perpendicularDistance(pts[i], pts[start], pts[end])
      if (dist > maxDist) {
        maxDist = dist
        index = i
      }
    }
    if (maxDist > eps) {
      keep[index] = true
      rdp(pts, start, index, eps, keep)
      rdp(pts, index, end, eps, keep)
    }
  }

  const keep = Array(points.length).fill(false)
  keep[0] = keep[points.length - 1] = true
  rdp(points, 0, points.length - 1, epsilon, keep)

  return points.filter((_, i) => keep[i])
}



const DrawingCanvas = forwardRef(({ colorRef, brush, brushSize }, ref) => {
  const stageRef = useRef(null)
  const mainLayerRef = useRef(null)
  const previewLayerRef = useRef(null)

  const isDrawing = useRef(false)
  const lastPointRef = useRef(null)
  const rafRef = useRef(null)

  // Line brush refs
  const currentLineRef = useRef(null)
  const linePointsRef = useRef([])

  // Stamp brush refs
  const stampCanvas = useRef(null)
  const stampCtx = useRef(null)
  const stampImg = useRef(null)

  const undoStack = useRef([])
  const redoStack = useRef([])

  const pointerIdRef = useRef(null)

  // Global pointer up handler to end strokes even if pointer leaves canvas
  useEffect(() => {
    const handleGlobalPointerUp = () => {
      const stage = stageRef.current
      if (!stage) return

      if (pointerIdRef.current != null && stage.container().releasePointerCapture) {
        stage.container().releasePointerCapture(pointerIdRef.current)
        pointerIdRef.current = null
      }

      isDrawing.current = false
      rafRef.current = null
      lastPointRef.current = null
      currentLineRef.current = null
      linePointsRef.current = []
      stampCanvas.current = null
      stampCtx.current = null
      stampImg.current = null
    }

    window.addEventListener("pointerup", handleGlobalPointerUp)

    return () => {
      window.removeEventListener("pointerup", handleGlobalPointerUp)
    }
  }, [])

  // Start a new stroke on pointer down
  const handlePointerDown = (e) => {
    const evt = e.evt || e
    const pos = stageRef.current.getPointerPosition()
    if (!pos) return
    isDrawing.current = true
    lastPointRef.current = pos
    linePointsRef.current = [pos]

    pointerIdRef.current = evt.pointerId

    if (brush.mode === "line") {
      const group = new Konva.Group({ listening: false })
      previewLayerRef.current.add(group)

      const line = brush.startStroke({
        group,
        color: colorRef.current,
        size: brushSize,
        point: pos,
      })

      currentLineRef.current = line
    }

    if (brush.mode === "stamp") {
      brush.startStroke({
        size: brushSize,
        color: colorRef.current,
      })

      const { width, height } = stageRef.current.size()

      const canvas = document.createElement("canvas")
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext("2d")

      stampCanvas.current = canvas
      stampCtx.current = ctx

      const image = new Konva.Image({
        image: canvas,
        listening: false,
      })

      stampImg.current = image
      previewLayerRef.current.add(image)

      brush.drawStroke({
        ctx,
        size: brushSize,
        from: pos,
        to: pos,
      })
    }

    previewLayerRef.current.batchDraw()
  }

  // Accumulate points on pointer move
  const handlePointerMove = () => {
    if (!isDrawing.current) return
    const pos = stageRef.current.getPointerPosition()
    const last = lastPointRef.current
    if (!pos || !last) return

    const dx = pos.x - last.x
    const dy = pos.y - last.y
    const dist = Math.hypot(dx, dy)
    const MIN_DIST = brushSize * 0.3
    if (dist < MIN_DIST) return

    linePointsRef.current.push(pos)

    if (!rafRef.current) {
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null

        // Line brush: add points to current line
        if (brush.mode === "line" && currentLineRef.current) {
          const points = currentLineRef.current.points()
          points.push(pos.x, pos.y)
          currentLineRef.current.points(points)
        }

        // Stamp brushes: draw stamps along the path
        if (brush.mode === "stamp" && stampCtx.current) {
          brush.drawStroke({
            ctx: stampCtx.current,
            size: brushSize,
            from: lastPointRef.current,
            to: pos,
          })
        }

        lastPointRef.current = pos
        previewLayerRef.current.batchDraw()
      })
    }
  }

  // End the stroke on pointer up
  const handlePointerUp = async () => {
    if (!isDrawing.current) return
    isDrawing.current = false
    if (linePointsRef.current.length === 0) return

    const simplifiedPoints = simplifyRDP(linePointsRef.current, 1.5)

    // Decimate and smooth points for lazy spline effect
    if (brush.mode === "line" && currentLineRef.current) {
      currentLineRef.current.points(
        simplifiedPoints.flatMap((p) => [p.x, p.y])
      )
    }

    if (brush.mode === "stamp" && stampImg.current) {
      const ctx = stampCtx.current
      const canvas = stampCanvas.current
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      for (let i = 1; i < simplifiedPoints.length; i++) {
        const from = simplifiedPoints[i - 1]
        const to = simplifiedPoints[i]

        if (!from || !to) continue

        brush.drawStroke({
          ctx,
          size: brushSize,
          from,
          to,
        })
      }
    }

    // Flatten preview to main layer
    const layer = previewLayerRef.current
    const rect = layer.getClientRect()

    const dataUrl = layer.toDataURL({
      x: rect.x,
      y: rect.y,
      width: rect.width,
      height: rect.height,
      pixelRatio: 2,
    })

    const img = new Image()
    img.src = dataUrl
    await img.decode()

    const konvaImg = new Konva.Image({
      image: img,
      x: rect.x,
      y: rect.y,
      width: rect.width,
      height: rect.height,
      listening: false,
    })

    mainLayerRef.current.add(konvaImg)
    undoStack.current.push(konvaImg)
    redoStack.current = []

    // Cleanup
    previewLayerRef.current.destroyChildren()
    previewLayerRef.current.batchDraw()
    mainLayerRef.current.batchDraw()

    currentLineRef.current = null
    linePointsRef.current = []
    stampCanvas.current = null
    stampCtx.current = null
    stampImg.current = null
    lastPointRef.current = null
  }

  // Expose undo/redo methods to parent
  useImperativeHandle(ref, () => ({
    undo: () => {
      const node = undoStack.current.pop()
      if (!node) return
      node.remove()
      redoStack.current.push(node)
      mainLayerRef.current.batchDraw()
    },

    redo: () => {
      const node = redoStack.current.pop()
      if (!node) return
      mainLayerRef.current.add(node)
      undoStack.current.push(node)
      mainLayerRef.current.batchDraw()
    },

    getStage: () => stageRef.current,
  }))

  return (
    <div className="drawing-canvas-wrapper">
      <Stage 
        ref={stageRef}
        className="drawing-canvas-stage"
        width={window.innerWidth}
        height={window.innerHeight}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onPointerLeave={handlePointerUp}
      >
        <Layer ref={mainLayerRef} />
        <Layer ref={previewLayerRef} />
      </Stage>
    </div>
  )
})

export default React.memo(DrawingCanvas)